import {Box, Button, IconButton, ListItem, Stack, TextField, Tooltip, Typography} from "@mui/material";
import React, {useMemo, useState} from "react";
import {getImageUrl} from "../../helpers/getImageUrl";
import ChatImage from "./ChatImage";
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import PlatformBadge from "./PlatformBadge";

const HTML_TAG_RE = /<\/?[a-z][\s\S]*>/i;
const ALLOWED_TAGS = new Set([
    'a', 'b', 'br', 'code', 'em', 'i', 'li', 'ol', 'p', 'pre', 's', 'span', 'strong', 'u', 'ul'
]);

const sanitizeMessageHtml = (html) => {
    if (!html || typeof window === 'undefined' || !window.DOMParser) {
        return html || '';
    }

    const doc = new window.DOMParser().parseFromString(html, 'text/html');

    const sanitizeNode = (node) => {
        if (node.nodeType !== window.Node.ELEMENT_NODE) return;

        const tag = node.tagName.toLowerCase();
        if (!ALLOWED_TAGS.has(tag)) {
            const parent = node.parentNode;
            if (!parent) return;

            while (node.firstChild) {
                parent.insertBefore(node.firstChild, node);
            }
            parent.removeChild(node);
            return;
        }

        [...node.attributes].forEach((attr) => {
            const name = attr.name.toLowerCase();
            if (tag === 'a' && (name === 'href' || name === 'target' || name === 'rel')) return;
            node.removeAttribute(attr.name);
        });

        if (tag === 'a') {
            const href = (node.getAttribute('href') || '').trim();
            const isSafeHref = /^(https?:|mailto:|tel:|\/|#)/i.test(href);
            if (!isSafeHref) {
                node.removeAttribute('href');
            }

            if (node.getAttribute('target') === '_blank') {
                node.setAttribute('rel', 'noopener noreferrer');
            }
        }
    };

    const walker = doc.createTreeWalker(doc.body, window.NodeFilter.SHOW_ELEMENT);
    const elements = [];
    while (walker.nextNode()) elements.push(walker.currentNode);
    elements.forEach(sanitizeNode);

    return doc.body.innerHTML;
};

const ChatMessage = ({
    isPatient,
    message,
    authorName,
    datetime,
    media,
    platform = null,
    canEdit = false,
    onEdit,
    editedAt = null,
    rowBgColor,
}) => {
    const senderLabel = isPatient ? 'Пациент' : 'Менеджер';
    const senderLabelColor = isPatient ? '#5e35b1' : '#1565c0';
    const messageBoxStyles = {
        bgcolor: isPatient ? '#f1ecf7' : '#ede4fb',
        border: '1px solid',
        borderColor: isPatient ? '#e0d6ee' : '#d6c4f4',
        borderRadius: 2.5,
        borderTopLeftRadius: isPatient ? 4 : 20,
        borderTopRightRadius: isPatient ? 20 : 4,
        px: 1.25,
        py: 0.75,
        maxWidth: 700,
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)',
    };

    // Стили для текста даты
    const dateTextStyles = {
        fontSize: '12px',
        color: '#9e9e9e',
        mx: 1,
        alignSelf: 'flex-end',
        whiteSpace: 'nowrap',
    };

    const [hovered, setHovered] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState(message || '');

    const isHtmlMessage = useMemo(() => HTML_TAG_RE.test(message || ''), [message]);
    const sanitizedHtmlMessage = useMemo(
        () => (isHtmlMessage ? sanitizeMessageHtml(message) : ''),
        [isHtmlMessage, message]
    );

    const editedLabel = useMemo(() => {
        if (!editedAt) return null;
        try {
            const d = new Date(editedAt);
            if (isNaN(d.getTime())) return '(ред.)';
            return `(ред. ${d.toLocaleString()})`;
        } catch {
            return '(ред.)';
        }
    }, [editedAt]);

    const renderMedia = () => {
        if (!media) return null;

        const mediaArr = Array.isArray(media) ? media : [media];
        if (mediaArr.length === 0) return null;

        return (
            <Stack direction="row" sx={{mt: 1, flexWrap: 'wrap', alignItems: 'inherit', justifyContent: 'inherit'}}>
                {mediaArr.map((m, idx) => {
                    if (!m?.key || !m?.type) return null;
                    const fileUrl = getImageUrl(m.key);

                    if (m.type === 'photo') {
                        return (
                            <ChatImage key={`${m.key}-${idx}`} src={fileUrl} alt="attached" />
                        );
                    }

                    return (
                        <Box key={`${m.key}-${idx}`} sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 1,
                            border: '1px solid #e0e0e0',
                            overflow: 'hidden',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            bgcolor: '#fafafa'
                        }}>
                            <Button
                                variant="text"
                                color="inherit"
                                component="a"
                                href={fileUrl}
                                download
                                sx={{width: '100%', height: '100%', p: 0, minWidth: 0}}
                                aria-label="download document"
                            >
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: '100%',
                                    height: '100%'
                                }}>
                                    <InsertDriveFileIcon sx={{fontSize: 36, color: '#9e9e9e'}} />
                                </Box>
                            </Button>
                        </Box>
                    );
                })}
            </Stack>
        );
    };

    const hasText = Boolean(message) || isEditing;

    return (
        <ListItem
            sx={{
                justifyContent: isPatient ? "flex-start" : "flex-end",
                alignItems: "flex-start",
                ...(rowBgColor && { bgcolor: rowBgColor }),
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Stack>
                {hasText && (
                    <Stack direction="row" sx={{alignItems: 'flex-start', justifyContent: isPatient ? "flex-start" : "flex-end"}}>
                        {!isPatient && (
                            <Typography sx={dateTextStyles}>{datetime}</Typography>
                        )}
                        <Box sx={{...messageBoxStyles, position: 'relative'}}>
                            {/* Заголовок: кто пишет + платформа */}
                            <Stack direction="row" spacing={0.5} sx={{alignItems: 'center', mb: 0.25}}>
                                <Typography sx={{fontSize: 11, fontWeight: 600, color: senderLabelColor, letterSpacing: 0.2}}>
                                    {senderLabel}
                                </Typography>
                                {platform && <PlatformBadge platform={platform} size="medium"/>}
                            </Stack>
                            {!isEditing ? (
                                <>
                                    {isHtmlMessage ? (
                                        <Typography
                                            component="div"
                                            sx={{
                                                whiteSpace: 'break-spaces',
                                                tabSize: 4,
                                                overflowWrap: 'anywhere',
                                                wordBreak: 'break-word',
                                                '& p': {my: 0},
                                                '& ul, & ol': {my: 0, pl: 2.5},
                                                '& pre, & code': {whiteSpace: 'pre-wrap'},
                                                '& a': {color: 'inherit'},
                                            }}
                                            dangerouslySetInnerHTML={{__html: sanitizedHtmlMessage}}
                                        />
                                    ) : (
                                        <Typography
                                            sx={{
                                                whiteSpace: 'break-spaces',
                                                tabSize: 4,
                                                overflowWrap: 'anywhere',
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {message}
                                        </Typography>
                                    )}

                                    {/* meta row: author and edited marker */}
                                    {(!isPatient || editedLabel) && (
                                        <Stack direction="row" spacing={1} sx={{alignItems: 'center', mt: 0.25}}>
                                            {!isPatient && (
                                                <Typography sx={{fontSize: 11, color: '#9e9e9e', fontStyle: 'italic'}}>
                                                    {authorName}
                                                </Typography>
                                            )}
                                            {editedLabel && (
                                                <Typography sx={{fontSize: 11, color: '#bdbdbd'}}>
                                                    {editedLabel}
                                                </Typography>
                                            )}
                                        </Stack>
                                    )}

                                    {canEdit && hovered && (
                                        <Tooltip title="Редактировать">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setDraft(message || '');
                                                    setIsEditing(true);
                                                }}
                                                sx={{
                                                    position: 'absolute',
                                                    top: 4,
                                                    right: 4,
                                                    bgcolor: 'rgba(255,255,255,0.6)'
                                                }}
                                            >
                                                <EditIcon fontSize="inherit"/>
                                            </IconButton>
                                        </Tooltip>
                                    )}
                                </>
                            ) : (
                                <Stack direction="row" spacing={1} sx={{alignItems: 'flex-start'}}>
                                    <TextField
                                        size="small"
                                        multiline
                                        maxRows={6}
                                        value={draft}
                                        onChange={(e) => setDraft(e.target.value)}
                                        sx={{minWidth: 240, background: '#fff', borderRadius: 1}}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' && !e.shiftKey) {
                                                e.preventDefault();
                                                if (draft.trim() && onEdit) onEdit(draft.trim());
                                                setIsEditing(false);
                                            } else if (e.key === 'Escape') {
                                                e.preventDefault();
                                                setIsEditing(false);
                                                setDraft(message || '');
                                            }
                                        }}
                                    />
                                    <Stack direction="row" spacing={0.5}>
                                        <Tooltip title="Сохранить">
                                            <IconButton
                                                color="primary"
                                                size="small"
                                                onClick={() => {
                                                    if (draft.trim() && onEdit) onEdit(draft.trim());
                                                    setIsEditing(false);
                                                }}
                                            >
                                                <CheckIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Отменить">
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setIsEditing(false);
                                                    setDraft(message || '');
                                                }}
                                            >
                                                <CloseIcon fontSize="small"/>
                                            </IconButton>
                                        </Tooltip>
                                    </Stack>
                                </Stack>
                            )}
                        </Box>
                        {isPatient && <Typography sx={dateTextStyles}>{datetime}</Typography>}
                    </Stack>
                )}

                {/* Media row: if no text, include date next to media */}
                {media && (
                    hasText ? (
                        <Box sx={{display: "flex", justifyContent: isPatient ? 'flex-start' : 'flex-end'}}>
                            {renderMedia()}
                        </Box>
                    ) : (
                        <Stack direction="row" sx={{alignItems: 'flex-start', gap: 1}}>
                            {!isPatient && (
                                <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                                    <Typography sx={dateTextStyles}>{datetime}</Typography>
                                    {editedLabel && (
                                        <Typography sx={{fontSize: "12px", color: "#9e9e9e", m: 1}}>
                                            {editedLabel}
                                        </Typography>
                                    )}
                                </Stack>
                            )}
                            <Box sx={{display: 'flex', justifyContent: isPatient ? 'flex-start' : 'flex-end'}}>
                                {renderMedia()}
                            </Box>
                            {isPatient && (
                                <Stack direction="row" spacing={1} sx={{alignItems: 'center'}}>
                                    <Typography sx={dateTextStyles}>{datetime}</Typography>
                                    {editedLabel && (
                                        <Typography sx={{fontSize: "12px", color: "#9e9e9e", m: 1}}>
                                            {editedLabel}
                                        </Typography>
                                    )}
                                </Stack>
                            )}
                        </Stack>
                    )
                )}
            </Stack>
        </ListItem>
    );
};

export default ChatMessage;
